import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import { EMPTY_FN, EMPTY_OBJ } from '~/constants';
import { monacoStore, Monaco, editor } from './monaco-editor';
import ResizeObserver from 'rc-resize-observer';
import { observer } from 'mobx-react-lite';
import { Spin } from '@fx-ui/fine-design';
import Intl from '@fx-ui/intl';
import { DEFAULT_EDITOR_OPTION } from './constant';

type CommonEditorOption = editor.IEditorOptions & editor.IGlobalEditorOptions;

interface IFieldRichTextProps {
    value?: string;
    onChange?: (v: string) => void;
    /** monaco-editor的配置 */
    editorOption?: CommonEditorOption;
    language: string;
    className?: string;
    editorDidMount?: (instance: editor.IStandaloneCodeEditor, monaco: Monaco) => void;
    editorWillUnmount?: (instance: editor.IStandaloneCodeEditor) => void;
}

export interface CodeEditorRef {
    /** 编辑器实例 */
    instance?: editor.IStandaloneCodeEditor;
}

/**
 * 基于monaco-editor的web代码编辑器
 *
 * @param props IFieldRichTextProps
 * @param ref CodeEditorRef
 * @function
 */
const CodeEditor: React.ForwardRefRenderFunction<CodeEditorRef, IFieldRichTextProps> = (
    props,
    ref
) => {
    const {
        value = '',
        language,
        className,
        onChange = EMPTY_FN,
        editorOption = EMPTY_OBJ,
        editorDidMount = EMPTY_FN,
        editorWillUnmount = EMPTY_FN
    } = props;
    const editorInstanceRef = useRef<editor.IStandaloneCodeEditor>();
    const containerRef = useRef<HTMLDivElement>(null);

    const { monaco, isLoading } = monacoStore;

    useEffect(() => {
        const editor = editorInstanceRef.current;
        const model = editor?.getModel();
        if (!editor || !model) {
            return;
        }
        const currentValue = model.getValue();
        if (currentValue !== value) {
            model.pushEditOperations(
                null,
                [
                    {
                        range: model.getFullModelRange(),
                        text: value
                    }
                ],
                () => []
            );
        }
    }, [value]);

    useEffect(() => {
        const model = editorInstanceRef?.current?.getModel();
        if (!model || !monaco) {
            return;
        }
        monaco.editor.setModelLanguage(model, language);
    }, [language]);

    useEffect(() => {
        editorInstanceRef.current?.updateOptions(editorOption);
    }, [editorOption]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !monaco) {
            return;
        }

        const model = monaco.editor.createModel(value, language);
        const instance = monaco.editor.create(container, {
            value,
            model,
            ...DEFAULT_EDITOR_OPTION,
            ...editorOption
        });

        model.onDidChangeContent(() => {
            onChange(model.getValue());
        });
        editorInstanceRef.current = instance;
        editorDidMount(instance, monaco);

        return () => {
            editorWillUnmount(instance);
            instance.dispose();
            model.dispose();
        };
    }, [monaco]);

    useImperativeHandle(ref, () => ({ instance: editorInstanceRef.current }));

    if (!monaco) {
        return (
            <Spin
                size="large"
                maskType="dark"
                // 非loading且monaco为空时，即视为浏览器不兼容
                description={!isLoading && Intl.get('privatePlugin.editor.incompatible')}
            />
        );
    }

    return (
        <ResizeObserver
            onResize={() => {
                const instance = editorInstanceRef.current;
                instance && instance.layout();
            }}
        >
            <div className={classNames('fx-monaco-editor', className)} ref={containerRef} />
        </ResizeObserver>
    );
};

export default observer(forwardRef(CodeEditor));
